import type { FC } from 'react';
import { useQuery } from '@tanstack/react-query';

import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { usePassportVerificationStatus } from 'hooks/usePassportVerification';
import { useGetCurrentProfile } from 'modules/Profile';
import { api, type ApiRequestConfig } from 'api/v1/api';

import { TutorNotice } from './TutorNotice';

type Readiness = {
  profile_complete: boolean;
  message: string;
};

export const PassportVerificationBanner: FC = () => {
  const { data: session } = useAuthSession();
  const { data: profile } = useGetCurrentProfile();
  const { data: passportStatus, isLoading: isPassportLoading } = usePassportVerificationStatus();

  const { data: readiness, isLoading: isReadinessLoading } = useQuery<Readiness>({
    queryKey: ['tutor-profile-readiness'],
    queryFn: () =>
      api
        .get('/profile/tutor/readiness', {
          skipGlobalErrorHandler: true,
        } as ApiRequestConfig)
        .then((r) => r.data),
    enabled: isAuthSessionActive(session),
    staleTime: 60_000,
    retry: false,
  });

  if (!isAuthSessionActive(session)) {
    return null;
  }

  if (profile?.profile_verified || passportStatus?.status === 'approved') {
    return null;
  }

  if (isPassportLoading || isReadinessLoading) {
    return null;
  }

  if (!readiness?.profile_complete) {
    return null;
  }

  if (passportStatus?.photos_purged && passportStatus.status === 'pending_review') {
    return (
      <TutorNotice
        tone='note'
        title='Срок проверки паспорта истек'
        text='Заявка снята с модерации. Загрузите фото паспорта заново.'
        linkTo='/passport-confirm'
        linkLabel='Отправить снова'
      />
    );
  }

  const rejectedPhotos =
    passportStatus?.photos?.filter((photo) => photo.status === 'rejected') ?? [];

  if (passportStatus?.status === 'pending_review' && rejectedPhotos.length > 0) {
    const numbers = rejectedPhotos.map((p) => p.number).join(', ');
    const comment = rejectedPhotos.find((p) => p.comment)?.comment;
    return (
      <TutorNotice
        tone='note'
        title={`Отклонено фото: ${numbers} из 3`}
        text={
          comment
            ? `Причина: ${comment}. Переснимите только отклоненные фото.`
            : 'Переснимите только отклоненные фото паспорта.'
        }
        linkTo='/passport-confirm'
        linkLabel='Исправить и отправить'
      />
    );
  }

  if (passportStatus?.status === 'pending_review') {
    return (
      <TutorNotice
        title='Документы на проверке'
        text='Паспорт отправлен модератору. После одобрения в профиле появится отметка о подтверждении документов.'
      />
    );
  }

  if (passportStatus?.status === 'rejected') {
    return (
      <TutorNotice
        tone='note'
        title='Паспорт не принят'
        text='Проверьте комментарии модератора и загрузите фото заново.'
        linkTo='/passport-confirm'
        linkLabel='Загрузить снова'
      />
    );
  }

  return (
    <TutorNotice
      title='Подтвердите документы'
      text='Документы пока не отправлены. Для проверки нужны разворот паспорта, страница регистрации и селфи.'
      linkTo='/passport-confirm'
      linkLabel='Перейти к проверке'
    />
  );
};
